import { useNavigate } from 'react-router-dom'
import { useState, useRef, useEffect } from 'react'
import { ChevronDownIcon } from 'lucide-react'

import { useTestUsers } from '@/hooks/queries/auth/index'
import { useAddTestMemory, useTestLogin } from '@/hooks/queries/auth/mutation'
import { useAuthStore } from '@/store/authStore'
import TagItem from '@/components/common/tag/TagItem'

export default function TestLoginPage() {
  const navigate = useNavigate()
  const { data: testUsers } = useTestUsers()
  const [isOpen, setIsOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<{ user_id: number; nickname: string } | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const selectRef = useRef<HTMLDivElement>(null)

  const { mutate: testLogin } = useTestLogin({
    onSuccess: async data => {
      // 메모리에만 저장
      useAuthStore.getState().setAccessToken(data.data.access_token)
      useAuthStore.getState().setUserInfo(data.data.user)
      navigate(`/auth/test-signup-check?user_id=${data.data.user.user_id}`)
    }
  })

  const { mutate: addTestMemory } = useAddTestMemory({})

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSelectUser = (user: { user_id: number; nickname: string; is_new: boolean }) => {
    setSelectedUser(user)
    setIsOpen(false)

    if (user.is_new) addTestMemory()
    testLogin({ user_id: user.user_id })
  }

  return (
    <div className='w-full h-full flex items-center justify-center bg-gradient-to-b from-[#FFF9F2] to-white'>
      <div className='w-full max-w-[400px] px-6 py-8'>
        <div className='space-y-6'>
          {/* 타이틀 */}
          <div className='text-center mb-8'>
            <h2 className='text-[24px] font-[700] text-[#020202] mb-2'>테스트 계정으로 로그인</h2>
            <p className='text-[14px] text-[#7D7D7D]'>아래 계정 중 하나를 선택해주세요</p>
          </div>

          {/* 드롭다운 셀렉트 */}
          <div className='relative'>
            <div
              ref={selectRef}
              onClick={() => setIsOpen(!isOpen)}
              className='w-full px-6 py-3 rounded-2xl bg-white/70 backdrop-blur-sm border-2 border-[#FFA236]/20 focus:border-[#FFA236] focus:outline-none focus:ring-2 focus:ring-[#FFA236]/50 transition-all duration-300 text-center text-[18px] font-[500] cursor-pointer flex items-center justify-between hover:bg-white/80'
            >
              <span className={`flex-1 ${!selectedUser ? 'text-gray-400' : 'text-gray-800'}`}>
                {selectedUser ? selectedUser.nickname : '테스트 계정 선택'}
              </span>
              <ChevronDownIcon
                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              />
            </div>

            {/* 드롭다운 리스트 */}
            {isOpen && testUsers?.data && (
              <div
                ref={dropdownRef}
                className='absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-2xl shadow-lg max-h-60 overflow-y-auto'
              >
                {testUsers.data.reverse().map(user => (
                  <div
                    key={user.user_id}
                    onClick={() => handleSelectUser(user)}
                    className='px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-[#FFF9F2] first:rounded-t-2xl last:rounded-b-2xl transition-colors duration-200 flex justify-between'
                  >
                    <div className='font-medium text-center text-gray-800'>{user.nickname}</div>
                    {user.is_new && <TagItem tag='new' />}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 안내 문구 */}
          <div className='mt-8 text-center'>
            <p className='text-[12px] text-gray-500'>테스트 환경에서만 사용 가능한 기능입니다</p>
          </div>
        </div>
      </div>
    </div>
  )
}
